import React, { useState, useEffect, useRef } from 'react';
import AuthService from '../utils/authService';

const LoginPage = ({ onAuthenticated }) => {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [securityInfo, setSecurityInfo] = useState({});
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  const authService = useRef(new AuthService()).current;
  const passwordInputRef = useRef(null);

  useEffect(() => {
    // Focus password input on mount
    if (passwordInputRef.current) {
      passwordInputRef.current.focus();
    }
    
    // Update security info
    updateSecurityInfo();
    
    // Set up periodic security info updates
    const interval = setInterval(updateSecurityInfo, 1000);
    return () => clearInterval(interval);
  }, []);

  const updateSecurityInfo = () => {
    const info = authService.getSecurityInfo();
    setSecurityInfo(info);
    
    // Clear error if lockout period has ended
    if (error && !info.isLockedOut && info.failedAttempts > 0) {
      setError('');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (loading || securityInfo.isLockedOut) return;

    setLoading(true);
    setError('');

    try {
      await authService.authenticate(password);
      setPassword('');
      onAuthenticated();
    } catch (err) {
      setError(err.message);
      setPassword('');
      updateSecurityInfo();
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (loading) return;

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await authService.setPassword(newPassword);
      setNewPassword('');
      setConfirmPassword('');
      setShowPasswordChange(false);
      setError('');
      updateSecurityInfo();
      alert('Password changed successfully');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getRemainingLockoutTime = () => {
    if (!securityInfo.isLockedOut) return '';
    const minutes = Math.floor(securityInfo.lockoutRemaining / 60);
    const seconds = securityInfo.lockoutRemaining % 60;
    return `${minutes}m ${seconds}s`;
  };

  const getSecurityStatus = () => {
    if (securityInfo.isLockedOut) {
      return {
        color: 'red',
        message: `Account locked for ${getRemainingLockoutTime()}`
      };
    } else if (securityInfo.failedAttempts > 0) {
      const remaining = securityInfo.maxAttempts - securityInfo.failedAttempts;
      return {
        color: 'orange',
        message: `${remaining} attempts remaining before lockout`
      };
    } else {
      return {
        color: 'green',
        message: 'Ready for login'
      };
    }
  };

  const status = getSecurityStatus();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto h-16 w-16 bg-blue-600 rounded-full flex items-center justify-center mb-6">
            <svg className="h-8 w-8 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
            </svg>
          </div>
          <h2 className="text-3xl font-bold text-gray-900">Skills Mapping App</h2>
          <p className="mt-2 text-sm text-gray-600">Secure access to your skills data</p>
        </div>

        {/* Privacy Assurance */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
          <div className="flex items-center space-x-2">
            <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
            </svg>
            <span className="text-sm font-medium text-green-800">All data stays on your device</span>
          </div>
        </div>

        {/* Security Status */}
        <div className={`border rounded-lg p-3 ${
          status.color === 'red' ? 'bg-red-50 border-red-200' :
          status.color === 'orange' ? 'bg-orange-50 border-orange-200' :
          'bg-green-50 border-green-200'
        }`}>
          <div className="flex items-center space-x-2">
            <div className={`w-2 h-2 rounded-full ${
              status.color === 'red' ? 'bg-red-500' :
              status.color === 'orange' ? 'bg-orange-500' :
              'bg-green-500'
            }`}></div>
            <span className={`text-sm font-medium ${
              status.color === 'red' ? 'text-red-800' :
              status.color === 'orange' ? 'text-orange-800' :
              'text-green-800'
            }`}>
              {status.message}
            </span>
          </div>
        </div>

        {/* Login Form */}
        {!showPasswordChange ? (
          <form className="mt-8 space-y-6" onSubmit={handleLogin}>
            <div className="bg-white shadow-lg rounded-lg p-8">
              {/* Password Input */}
              <div>
                <label htmlFor="password" className="sr-only">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  ref={passwordInputRef}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading || securityInfo.isLockedOut}
                  className="relative block w-full px-3 py-3 border border-gray-300 rounded-md placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:z-10 text-lg disabled:bg-gray-50 disabled:cursor-not-allowed"
                  placeholder="Enter password"
                  maxLength={128}
                />
              </div>

              {/* Error Message */}
              {error && (
                <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-3">
                  <div className="flex items-center">
                    <svg className="h-5 w-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                </div>
              )}

              {/* Login Button */}
              <div className="mt-6">
                <button
                  type="submit"
                  disabled={loading || securityInfo.isLockedOut || !password.trim()}
                  className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? (
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                    </svg>
                  )}
                  {loading ? 'Authenticating...' : 'Sign In'}
                </button>
              </div>

              {/* Default Password Notice */}
              {!securityInfo.hasCustomPassword && (
                <div className="mt-4 bg-orange-50 border border-orange-200 rounded-md p-3">
                  <p className="text-xs text-orange-700">
                    <strong>Default password:</strong> admin123 (click "Change Password" to set a custom password)
                  </p>
                </div>
              )}
            </div>

            {/* Change Password Link */}
            <div className="text-center">
              <button
                type="button"
                onClick={() => setShowPasswordChange(true)}
                className="text-sm text-blue-600 hover:text-blue-500 font-medium"
              >
                Change Password
              </button>
            </div>
          </form>
        ) : (
          /* Password Change Form */
          <form className="mt-8 space-y-6" onSubmit={handlePasswordChange}>
            <div className="bg-white shadow-lg rounded-lg p-8">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Change Password</h3>
              
              <div className="space-y-4">
                <div>
                  <label htmlFor="new-password" className="block text-sm font-medium text-gray-700">
                    New Password
                  </label>
                  <input
                    id="new-password"
                    name="new-password"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    disabled={loading}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50"
                    placeholder="Enter new password"
                    minLength={6}
                    maxLength={128}
                  />
                </div>

                <div>
                  <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700">
                    Confirm Password
                  </label>
                  <input
                    id="confirm-password"
                    name="confirm-password"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={loading}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50"
                    placeholder="Confirm new password"
                    minLength={6}
                    maxLength={128}
                  />
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              {/* Buttons */}
              <div className="mt-6 flex space-x-3">
                <button
                  type="submit"
                  disabled={loading || !newPassword.trim() || !confirmPassword.trim()}
                  className="flex-1 py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Updating...' : 'Update Password'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordChange(false);
                    setNewPassword('');
                    setConfirmPassword('');
                    setError('');
                  }}
                  disabled={loading}
                  className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        )}

        {/* Security Info */}
        <div className="text-center text-xs text-gray-500 space-y-1">
          <p>Protected against brute force attacks</p>
          <p>Session expires automatically for security</p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;